import {
  Component,
  Input,
  ContentChildren,
  QueryList,
  NgModule,
  AfterContentInit,
  ViewChild,
  ElementRef,
  OnInit,
  OnDestroy,
  ChangeDetectorRef
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { DomHandler } from '../../utils/domhandler';
import { PrimeTemplate, SharedModule } from '../../utils/shared';
import { addResizeListener, removeResizeListener } from '../../utils/resize-event';

/**
 * 两栏分隔条组件
 * @remarks
 * 用于分隔导航条面板以及主要区域面板，由PrimeNG的Splitter组件改写而来：
 * 1. 仅支持左右两列
 * 2. 以像素为单位
 * 2. 增加了类似VSCode的收起导航条的功能
 * 
 * 修改日志
 * 2022/07/25：保证右侧区域的最小宽度，如果页面大小变化导致右侧区域达不到最小宽度，则缩小左侧区域
 */
@Component({
  selector: 'ide-splitter',
  templateUrl: './splitter.html'
})
export class Splitter implements AfterContentInit, OnInit, OnDestroy {

  /** 分隔条宽度 */
  @Input() gutterSize: number = 4;

  /** 分隔条两侧面板的最小尺寸 */
  @Input() minSizes: number[] = [0, 0];

  /**
   * 突破最小尺寸该距离时隐藏左侧面板
   * @remarks 如果该距离小于等于0，则关闭此功能
   */
  @Input() hideCriticalDistance: number = 0;

  /** 初始状态下分隔条的位置，即左侧面板的初始大小 */
  @Input() initGutterPos: number;

  /**
   * 从隐藏导航栏的状态展开时导航栏的默认宽度
   * @remarks 该宽度不可为空
   */
  @Input() defaultGutterPos: number = 0;

  /** 左侧面板收折状态下的展开提示文本 */
  @Input() expandBarLabel: string = "展开导航面板";

  /** 是否彻底隐藏左侧的导航面板 */
  @Input() hideNavPanel: boolean = false;

  /** 两个面板的模板 */
  @ContentChildren(PrimeTemplate) templates: QueryList<any>;

  @ViewChild('container') containerViewChild: ElementRef;

  @ViewChild('gutter') gutterViewChild: ElementRef;

  gutterPos: number;

  dragging = false;

  startPos = null;

  startGutterPos = null;

  mouseMoveListener = null;

  mouseUpListener = null;

  /** 面板模板集合 */
  private panels = [];
  /** 左侧导航面板的模板 */
  get navPanelTemplate() {
    return this.panels[0];
  }
  /** 右侧主要面板的模板 */
  get mainPanelTemplate() {
    return this.panels[1];
  }

  get navPanelStyle() {
    const width = this.hideNavPanel ? 0 : this.gutterPos;
    return { width: `${width}px` };
  }

  get gutterStyle() {
    let position = Math.ceil(this.gutterPos - (this.gutterSize / 2));
    position = Math.max(0, position);
    return {
      width: `${this.gutterSize}px`,
      left: `${position}px`
    };
  }

  get mainPanelStyle() {
    return { marginLeft: `${this.gutterPos}px` };
  }

  get navPanelMinSize(): number {
    return ( this.minSizes && this.minSizes.length > 0 && this.minSizes[0] > 0 ) ? this.minSizes[0] : 0;
  }

  get mainPanelMinSize(): number {
    return ( this.minSizes && this.minSizes.length > 1 && this.minSizes[1] > 0) ? this.minSizes[1] : 0;
  }

  /** 左侧的面板是否处于收折状态 */
  get collapse(): boolean {
    return !this.dragging && this.gutterPos === 0;
  }

  constructor(
    private cd: ChangeDetectorRef
  ) { }

  /** 确保右侧区域达到了最小宽度 */
  private ensureMainPanelSize = () => {
    // 计算左右区域的大小
    const containerSize = DomHandler.getWidth(this.containerViewChild.nativeElement);
    const navPanelWidth = this.gutterPos;
    const mainPanelWidth = containerSize - navPanelWidth;
    if (mainPanelWidth >= this.mainPanelMinSize) {
      return;  // 如果已经满足要求，则不进行处理
    }
    if (containerSize < this.navPanelMinSize + this.mainPanelMinSize) {
      return;  // 如果总宽度小于左右区域最小宽度之和，则不进行处理
    }
    // 让右侧区域的宽度刚好等于其最小宽度
    this.gutterPos = containerSize - this.mainPanelMinSize;
  };

  ngOnInit(): void {
    this.gutterPos = this.initGutterPos;
    if (!this.defaultGutterPos) {
      this.defaultGutterPos = this.initGutterPos;
    }
  }

  ngAfterContentInit(): void {
    this.templates.forEach((item) => {
      this.panels.push(item.template);
    });
    addResizeListener(this.containerViewChild.nativeElement, this.ensureMainPanelSize);
    this.ensureMainPanelSize();
  }

  ngOnDestroy(): void {
    removeResizeListener(this.containerViewChild.nativeElement, this.ensureMainPanelSize);
  }

  /**
   * 切换左侧面板的収折状态
   * @param collapse 是否收折
   */
  public toggle(collapse?: boolean): void {
    if (collapse === true) {
      this.gutterPos = 0;
    } else if (collapse === false) {
      this.gutterPos = this.defaultGutterPos;
    } else {
      if (this.gutterPos === 0) {
        this.gutterPos = this.defaultGutterPos;
      } else {
        this.gutterPos = 0;
      }
    }
    this.cd.detectChanges();
  }

  /** 当左侧面板处于收折状态时，点击展开到默认位置 */
  handleExpand(): void {
    this.gutterPos = this.defaultGutterPos;
    this.ensureMainPanelSize();
    this.cd.detectChanges();
  }

  onGutterMouseDown(event: MouseEvent) {
    this.resizeStart(event);
    this.bindMouseListeners();
  }

  resizeStart(event: MouseEvent) {
    this.dragging = true;
    this.startPos = event.pageX;
    this.startGutterPos = this.gutterPos;
    DomHandler.addClass(this.gutterViewChild.nativeElement, 'ide-splitter-gutter-resizing');
    DomHandler.addClass(this.containerViewChild.nativeElement, 'ide-splitter-resizing');
  }

  bindMouseListeners() {
    if (!this.mouseMoveListener) {
      this.mouseMoveListener = (event: MouseEvent) => this.onResize(event);
      document.addEventListener('mousemove', this.mouseMoveListener);
    }
    if (!this.mouseUpListener) {
      this.mouseUpListener = () => {
        this.resizeEnd();
        this.unbindMouseListeners();
      }
      document.addEventListener('mouseup', this.mouseUpListener);
    }
  }

  onResize(event: MouseEvent) {
    const containerSize = DomHandler.getWidth(this.containerViewChild.nativeElement);
    const distance = event.pageX - this.startPos;
    let newNavPanelSize = this.startGutterPos + distance;
    newNavPanelSize = Math.max(0, newNavPanelSize);
    newNavPanelSize = Math.min(containerSize, newNavPanelSize);
    const newMainPanelSize = containerSize - newNavPanelSize;
    const isExpandingNav: boolean = distance > 0;  // 是否正在扩大导航面板
    // 如果主面板宽度达到最小值，则不再变化
    if (newMainPanelSize < this.mainPanelMinSize) {
      return;
    }
    // 如果导航面板宽度达到最小值，且未开启临界隐藏功能，则不再变化
    if (newNavPanelSize < this.navPanelMinSize && this.hideCriticalDistance <= 0) {
      return;
    }
    // 实现导航面板临界隐藏功能
    if (this.hideCriticalDistance > 0) {
      if (!isExpandingNav && newNavPanelSize < this.navPanelMinSize) {
        if (this.navPanelMinSize - newNavPanelSize >= this.hideCriticalDistance) {
          this.gutterPos = 0;
        } else {
          this.gutterPos = this.navPanelMinSize;
        }
        return;
      }
      if (isExpandingNav && this.gutterPos === 0 && newNavPanelSize < this.navPanelMinSize) {
        if (distance >= this.hideCriticalDistance) {
          this.gutterPos = this.navPanelMinSize;
          this.startGutterPos = this.gutterPos;
          this.startPos = this.gutterPos;
        }
        return;
      }
    }
    this.gutterPos = newNavPanelSize;
    this.cd.detectChanges();
  }

  resizeEnd() {
    DomHandler.removeClass(this.gutterViewChild.nativeElement, 'ide-splitter-gutter-resizing');
    DomHandler.removeClass(this.containerViewChild.nativeElement, 'ide-splitter-resizing');
    this.clear();
    this.cd.detectChanges();
  }

  unbindMouseListeners() {
    if (this.mouseMoveListener) {
      document.removeEventListener('mousemove', this.mouseMoveListener);
      this.mouseMoveListener = null;
    }
    if (this.mouseUpListener) {
      document.removeEventListener('mouseup', this.mouseUpListener);
      this.mouseUpListener = null;
    }
  }

  clear() {
    this.dragging = false;
    this.startPos = null;
    this.startGutterPos = null;
  }

}

@NgModule({
  imports: [CommonModule],
  exports: [Splitter, SharedModule],
  declarations: [Splitter]
})
export class SplitterModule { }
